import type {
  WeatherLimitBound, WeatherLimitInput, WeatherLimitMetric, WeatherLimitResult,
} from '../types/spray';

/**
 * The metrics a spray weather limit can be set on. Mirrors `METRICS` in the
 * backend's sprayWeatherLimitService — the server refuses anything else, so a
 * metric added there has to be added here to become pickable.
 *
 *   now       conditions at the spray (for a spray scheduled later, the
 *             forecast for its scheduled hours)
 *   forecast  the worst hour over the next `window_hours`; rain is the total
 */
export interface WeatherMetricDef {
  label: string;
  unit: string;
  kind: 'now' | 'forecast';
  bounds: WeatherLimitBound[];
  /** Shown under the picker — what a sensible number looks like. */
  hint?: string;
}

export const WEATHER_METRICS: Record<WeatherLimitMetric, WeatherMetricDef> = {
  wind_speed:           { label: 'Wind speed',           unit: 'km/h', kind: 'now',      bounds: ['max', 'min'], hint: 'Most labels: 3–20 km/h. A minimum guards against still-air inversions.' },
  wind_gust:            { label: 'Wind gusts',           unit: 'km/h', kind: 'now',      bounds: ['max'] },
  temperature:          { label: 'Temperature',          unit: '°C',   kind: 'now',      bounds: ['max', 'min'] },
  humidity:             { label: 'Humidity',             unit: '%',    kind: 'now',      bounds: ['max', 'min'] },
  delta_t:              { label: 'Delta T',              unit: '°C',   kind: 'now',      bounds: ['max', 'min'], hint: 'Preferred 2–8. Above 10 droplets evaporate; below 2 risks inversion.' },
  forecast_wind_speed:  { label: 'Forecast wind speed',  unit: 'km/h', kind: 'forecast', bounds: ['max', 'min'] },
  forecast_wind_gust:   { label: 'Forecast wind gusts',  unit: 'km/h', kind: 'forecast', bounds: ['max'] },
  forecast_temperature: { label: 'Forecast temperature', unit: '°C',   kind: 'forecast', bounds: ['max', 'min'], hint: 'e.g. max 30 °C over the next 72 h for a volatile herbicide.' },
  forecast_delta_t:     { label: 'Forecast Delta T',     unit: '°C',   kind: 'forecast', bounds: ['max', 'min'] },
  forecast_rain:        { label: 'Forecast rain',        unit: 'mm',   kind: 'forecast', bounds: ['max'], hint: 'Total over the window — for the product’s rainfast period.' },
};

export const WEATHER_METRIC_KEYS = Object.keys(WEATHER_METRICS) as WeatherLimitMetric[];

/** "22 km/h", "31 °C", "64%" */
export function formatWeatherValue(value: number | null | undefined, unit: string): string {
  if (value == null) return '—';
  if (unit === '%') return `${value}%`;
  return `${value} ${unit}`;
}

/** "max 15 km/h", "min 2 °C · next 72 h" */
export function describeLimit(limit: Pick<WeatherLimitInput, 'metric' | 'bound' | 'threshold' | 'window_hours'>): string {
  const def = WEATHER_METRICS[limit.metric];
  const base = `${limit.bound} ${formatWeatherValue(limit.threshold, def?.unit ?? '')}`;
  return def?.kind === 'forecast' && limit.window_hours ? `${base} · next ${limit.window_hours} h` : base;
}

export function sourceLabel(source: string | null | undefined): string | null {
  switch (source) {
    case 'station':    return 'Farm station';
    case 'open_meteo': return 'Open-Meteo';
    case 'mixed':      return 'Station + Open-Meteo';
    case 'forecast':   return 'Forecast';
    default:           return null;
  }
}

/** The breaches in a check, in the order the server listed the limits. */
export function breachedResults(results: WeatherLimitResult[] | undefined): WeatherLimitResult[] {
  return (results ?? []).filter(r => r.status === 'breach');
}
